import React from 'react';
import { Box, Text, HStack, Badge, Grid, GridItem, Collapse } from '@chakra-ui/react';
import { Hospital } from '../types';
import SimilarityMatches from './SimilarityMatches';
import InlineSearch from './InlineSearch';
import EditableField from './EditableField';
import HospitalCardHeader from './hospital-card/HospitalCardHeader';
import HospitalCardActions from './hospital-card/HospitalCardActions';
import { getStatusBadgeColor, getBorderColor } from '../utils/statusUtils';
import '../styles/HospitalSimilarity.css';

interface HospitalCardProps {
  hospital: Hospital;
  isExpanded: boolean;
  selectedMatchId?: string;
  onToggleExpansion: (id: string) => void;
  onStatusUpdate: (id: string, status: Hospital['status']) => void;
  onFieldUpdate?: (hospitalId: string, fieldName: string, value: string) => void;
  onReprocess?: (hospitalId: string) => void;
  onSelectMatch?: (hospitalId: string, matchId: string) => void;
}

const HospitalCard: React.FC<HospitalCardProps> = ({
  hospital,
  isExpanded,
  selectedMatchId,
  onToggleExpansion,
  onStatusUpdate,
  onFieldUpdate,
  onReprocess,
  onSelectMatch
}) => {

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="sm"
      borderLeft="4px solid"
      borderLeftColor={getBorderColor(hospital.status)}
      mb={4}
    >
      <Box
        p={5}
        cursor="pointer"
        onClick={() => onToggleExpansion(hospital._id)}
      >
        <HStack justify="space-between" align="flex-start">
          <HospitalCardHeader hospital={hospital} onFieldUpdate={onFieldUpdate} />
          <HStack spacing={3}>
            <Badge
              colorScheme={getStatusBadgeColor(hospital.status)}
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="full"
            >
              {hospital.status === 'new_hospital' ? 'New Hospital' : hospital.status}
            </Badge>
            <Text color="gray.400" fontSize="sm">
              {isExpanded ? '▲' : '▼'}
            </Text>
          </HStack>
        </HStack>
      </Box>

      <Collapse in={isExpanded}>
        <Box px={5} pb={5} borderTop="1px" borderColor="gray.100">
          <Grid templateColumns="repeat(4, 1fr)" gap={4} mt={4} fontSize="sm">
            <GridItem>
              <EditableField
                label="City"
                value={hospital.city || ''}
                hospitalId={hospital._id}
                fieldName="city"
                isRejected={hospital.status === 'rejected'}
                onSave={onFieldUpdate || (() => {})}
              />
            </GridItem>
            <GridItem>
              <EditableField
                label="District"
                value={hospital.district || ''}
                hospitalId={hospital._id}
                fieldName="district"
                isRejected={hospital.status === 'rejected'}
                onSave={onFieldUpdate || (() => {})}
              />
            </GridItem>
            <GridItem>
              <EditableField
                label="State"
                value={hospital.state || ''}
                hospitalId={hospital._id}
                fieldName="state"
                isRejected={hospital.status === 'rejected'}
                onSave={onFieldUpdate || (() => {})}
              />
            </GridItem>
            <GridItem>
              <EditableField
                label="Pincode"
                value={hospital.pincode || ''}
                hospitalId={hospital._id}
                fieldName="pincode"
                isRejected={hospital.status === 'rejected'}
                onSave={onFieldUpdate || (() => {})}
              />
            </GridItem>
          </Grid>

          {hospital.matching && hospital.matching.length > 0 && (
            <SimilarityMatches 
              matches={hospital.matching} 
              humanMatched={hospital.humanMatched}
              hospitalStatus={hospital.status}
              hospitalId={hospital._id}
              selectedMatchId={selectedMatchId}
              onSelectMatch={onSelectMatch}
            />
          )}

          {hospital.rejectionReason && (
            <Box mt={4} p={3} bg="red.50" border="1px" borderColor="red.200" borderRadius="md">
              <Text fontSize="sm" color="red.700">
                <Text as="span" fontWeight="bold">Rejection Reason:</Text> {hospital.rejectionReason}
                {hospital.reprocessCount !== undefined && (
                  <Text fontSize="xs" mt={1}>Reprocess attempts: {hospital.reprocessCount}</Text>
                )}
              </Text>
            </Box>
          )}

          <InlineSearch 
            hospitalId={hospital._id} 
            hospitalStatus={hospital.status}
            selectedMatchId={selectedMatchId}
            onSelectMatch={onSelectMatch}
          />

          <HospitalCardActions
            hospital={hospital}
            onStatusUpdate={onStatusUpdate}
            onReprocess={onReprocess}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default HospitalCard;